// Local FALLBACK config for the Replacement Logs table. The table prefers the
// org meta (digitalTwin.metaInfo.table.replacementLogsTable +
// filterConfigurations.replacementLogsTable) and falls back to these when a
// given org hasn't seeded them, so the tab keeps working out of the box.
// Figma: Column View lists 13 columns, 10 selected by default; Actions is
// always shown, fixed right, and outside the column-view checklist.

export const LOGS_MAX_COLUMNS = 16;

// dataIndex keys sortable server-side (contract §3.2 sortBy). The table maps
// these to the API sortBy names.
export const SORT_KEY_MAP = {
    recordedDate: "recordedAt",
    outgoingSerial: "outgoingSerial",
    incomingSerial: "incomingSerial",
    odometer: "odometerKm",
};

export const LOGS_COLUMNS_CONFIG = {
    requestID: {
        key: "requestID",
        dataIndex: "requestID",
        title: "Request ID",
        // Wide enough for a full 36-character uuid plus the copy button, since the
        // id is shown in full rather than truncated.
        width: 340,
        defaultValue: "-",
        copyToClipBoardTypeName: "Request ID",
        valueRenderingType: "text-with-copy",
    },
    recordedDate: {
        key: "recordedDate",
        dataIndex: "recordedDate",
        title: "Recorded Date",
        width: 150,
        sorter: true,
        fixed: "left",
        dateFormat: "DD MMM'YY",
        // A pack row leaves this to its request row, so it must read as blank
        // rather than as the timestamp renderer's "Not Available".
        defaultValue: "-",
        valueRenderingType: "timestamp",
    },
    // Request-level: "3 packs". Blank on a pack row, where the pack / string
    // columns say which pack it is.
    packCount: {
        key: "packCount",
        dataIndex: "packCount",
        title: "Packs",
        width: 110,
        defaultValue: "-",
        valueRenderingType: "text",
    },
    pack: { key: "pack", dataIndex: "pack", title: "Pack", width: 110, valueRenderingType: "text" },
    string: { key: "string", dataIndex: "string", title: "String", width: 110, valueRenderingType: "text" },
    incomingSerial: {
        key: "incomingSerial",
        dataIndex: "incomingSerial",
        title: "Incoming pack serial number",
        width: 280,
        sorter: true,
        defaultValue: "-",
        valueRenderingType: "text-with-copy",
    },
    outgoingSerial: {
        key: "outgoingSerial",
        dataIndex: "outgoingSerial",
        title: "Outgoing pack serial number",
        width: 280,
        sorter: true,
        defaultValue: "-",
        valueRenderingType: "text-with-copy",
    },
    problemReported: {
        key: "problemReported",
        dataIndex: "problemReported",
        title: "Problem reported",
        width: 220,
        defaultValue: "-",
        valueRenderingType: "text",
    },
    recordedBy: { key: "recordedBy", dataIndex: "recordedBy", title: "Recorded by", width: 150, valueRenderingType: "text" },
    odometer: {
        key: "odometer",
        dataIndex: "odometer",
        title: "Odometer (km)",
        width: 150,
        sorter: true,
        decimalPlaces: 0,
        defaultValue: "-",
        valueRenderingType: "number",
    },
    observation: { key: "observation", dataIndex: "observation", title: "Observation", width: 180, defaultValue: "-", valueRenderingType: "text" },
    outgoingWarranty: { key: "outgoingWarranty", dataIndex: "outgoingWarranty", title: "Outgoing pack warranty", width: 190, defaultValue: "-", valueRenderingType: "text" },
    // defaultVisible:false => present in Column View but unchecked by default.
    busHeldUp: {
        key: "busHeldUp",
        dataIndex: "busHeldUp",
        title: "Issue reported date",
        width: 180,
        dateFormat: "DD MMM'YY hh:mm A",
        defaultValue: "-",
        defaultVisible: false,
        valueRenderingType: "timestamp",
    },
    workStart: {
        key: "workStart",
        dataIndex: "workStart",
        title: "Work start date",
        width: 180,
        dateFormat: "DD MMM'YY hh:mm A",
        defaultValue: "-",
        defaultVisible: false,
        valueRenderingType: "timestamp",
    },
    workEnd: {
        key: "workEnd",
        dataIndex: "workEnd",
        title: "Work end date",
        width: 180,
        dateFormat: "DD MMM'YY hh:mm A",
        defaultValue: "-",
        defaultVisible: false,
        valueRenderingType: "timestamp",
    },
};

// Ordered so a row reads as the story of one replacement: when it happened, which
// request it was, how many packs and which, what came out and what went in, then
// why. Supporting detail follows.
//
// recordedDate leads because it is the fixed-left column and it carries the expand
// control -- a fixed column has to be leftmost, and Request ID is 340px wide, so
// putting that first pushed everything else off screen.
//
// String before Pack, and outgoing before incoming, to match how they are named and
// laid out everywhere else in the feature ("String 1 - Pack 3", OUTGOING -> INCOMING).
export const LOGS_COLUMN_ORDERING = [
    "recordedDate",
    "requestID",
    "packCount",
    "string",
    "pack",
    "outgoingSerial",
    "incomingSerial",
    "problemReported",
    "outgoingWarranty",
    "observation",
    "recordedBy",
    "odometer",
    "busHeldUp",
    "workStart",
    "workEnd",
];

// Filter definitions (which filters, labels, order, type). Option values for
// pack/string/reason/recordedBy are injected at runtime by the table (they are
// per-battery / dynamic). Keys must match mapSelectedFiltersToParams.
export const LOGS_FILTER_DEFS = [
    { key: "pack", label: "Pack", displayType: "multiSelectDropdown", type: "dynamic" },
    { key: "stringNo", label: "String", displayType: "multiSelectDropdown", type: "dynamic" },
    { key: "recordedDate", label: "Recorded date", displayType: "dateCalender", type: "static" },
    {
        key: "reason",
        label: "Problem reported",
        displayType: "multiSelectDropdown",
        type: "dynamic",
        note: "you can only filter using the predefined reasons",
    },
    { key: "recordedBy", label: "Recorded by", displayType: "multiSelectDropdown", type: "dynamic" },
];

// Default-visible column keys, in order: every column except those flagged
// defaultVisible:false. Works for both the org-meta and fallback configs.
export const getDefaultSelectedColumns = (columnsConfig, columnOrdering) =>
    (columnOrdering || []).filter(
        (key) => columnsConfig?.[key] && columnsConfig[key].defaultVisible !== false
    );
